import React from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';
import AIChatMessage from '../../../models/AIChatMessage';
import Contact from '../../../models/Contact';
import Investor from '../../../models/Investor';
import GenericChatWindow from '../../GenericChatWindow';
import { StyledButton } from '../../styles/StyledButtons';
import { StyledLabel } from '../../styles/styles';
import AdminAssistGroundedSuggestion from '../AdminAssistGroundedSuggestion';
import {
  GLOBAL_STARTER_PROMPTS,
  OPPORTUNITY_STARTER_PROMPTS,
} from './veraGlobalChatPromptOverrideUtils';

interface InvestorPreviewChatSectionProps {
  chatId: string;
  markdown: string;
  selectedContact: Contact | null;
  contactInvestments: Investor[];
  selectedInvestor: Investor | null;
  fundSearch: string;
  messages: AIChatMessage[];
  isProcessing: boolean;
  onFundSearchChange: (value: string) => void;
  onInvestorChange: (investor: Investor | null) => void;
  onSendMessage: (text: string) => void;
  onResetChat: () => void;
}

export default function InvestorPreviewChatSection({
  chatId,
  markdown,
  selectedContact,
  contactInvestments,
  selectedInvestor,
  fundSearch,
  messages,
  isProcessing,
  onFundSearchChange,
  onInvestorChange,
  onSendMessage,
  onResetChat,
}: InvestorPreviewChatSectionProps) {
  const filteredInvestments = contactInvestments.filter(
    (investor) =>
      !fundSearch ||
      (investor.fundOpportunity?.name ?? '').toLowerCase().includes(fundSearch.toLowerCase()),
  );

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <StyledLabel stacked>Preview Chat</StyledLabel>
        <StyledButton
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={onResetChat}
          disabled={messages.length === 0}
        >
          Reset Chat
        </StyledButton>
      </Box>

      {!selectedContact ? (
        <Box
          sx={{
            p: 2,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'warning.main',
            bgcolor: 'warning.light',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: 'warning.contrastText', fontWeight: 500 }}>
            ⚠️ Search and select a contact above to enable the preview chat.
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <Autocomplete
              options={filteredInvestments}
              getOptionLabel={(investor) =>
                investor.fundOpportunity?.name ?? `Fund ${investor.fundOpportunityId ?? ''}`
              }
              value={selectedInvestor}
              onChange={(_, value) => onInvestorChange(value)}
              inputValue={fundSearch}
              onInputChange={(_, value) => onFundSearchChange(value)}
              noOptionsText={
                contactInvestments.length === 0
                  ? 'No investments found for this contact'
                  : 'No matching funds'
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Add fund context (optional)"
                  placeholder="Search funds…"
                />
              )}
            />
          </Box>

          {selectedInvestor && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'primary.light',
                bgcolor: 'primary.50',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                {selectedInvestor.fundOpportunity?.name ?? 'Fund'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Status: <strong>{selectedInvestor.status}</strong>
                {selectedInvestor.committedInvestment != null && (
                  <>
                    {' '}
                    · Committed:{' '}
                    <strong>${selectedInvestor.committedInvestment.toLocaleString()}</strong>
                  </>
                )}
                {selectedInvestor.indicativeInvestment != null &&
                  selectedInvestor.committedInvestment == null && (
                    <>
                      {' '}
                      · Indicative:{' '}
                      <strong>${selectedInvestor.indicativeInvestment.toLocaleString()}</strong>
                    </>
                  )}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.5, display: 'block' }}
                >
                  Quick questions for this fund:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {OPPORTUNITY_STARTER_PROMPTS.map((prompt) => (
                    <Chip
                      key={prompt}
                      label={prompt}
                      size="small"
                      clickable
                      onClick={() => onSendMessage(prompt)}
                      disabled={isProcessing || !markdown.trim()}
                      sx={{ fontSize: 12 }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
            {GLOBAL_STARTER_PROMPTS.map((prompt) => (
              <Chip
                key={prompt}
                label={prompt}
                size="small"
                variant="outlined"
                clickable
                onClick={() => onSendMessage(prompt)}
                disabled={isProcessing || !markdown.trim()}
                sx={{ fontSize: 12 }}
              />
            ))}
          </Box>

          <GenericChatWindow
            messages={messages}
            isProcessing={isProcessing}
            onSubmit={async (text) => {
              await Promise.resolve(onSendMessage(text));
            }}
            inputDisabled={!markdown.trim() || isProcessing}
            placeholderPrompt={
              !markdown.trim()
                ? 'Enter a system prompt above to enable chat…'
                : 'Ask something as this contact…'
            }
            submitLabel="Ask Vera"
            messageAreaHeight={{ min: '220px', max: '420px' }}
            renderModelContent={(message) => (
              <AdminAssistGroundedSuggestion
                messageId={`vera-preview-${chatId}-${message.timestamp?.getTime() ?? 0}`}
                text={message.parts.map((part) => part.text).join('\n\n')}
                corpus={null}
                grounding={message.googleSearchGrounding}
                markdownVariant="inline"
              />
            )}
          />
        </>
      )}
    </Box>
  );
}
